export interface PublicPrescription {
  id: number
  comment: string
  sendDate: string
  expireDate: string
  cancelDate?: string
  fileKey?: string
  state?: number
  provinceCode?: string
  provinceName?: string
  cityCode?: string
  cityName?: string
  contryDivisionName?: string
  allowOtherProvinces?: boolean
  isSath3?: boolean
  paymentStatus?: number | null
  paymentStatusString?: string
  paymentAmount?: number
  paymentRequestedDate?: string
  paymentDueDate?: string
  paymentReceiptKey?: string
  paymentReceiptDate?: string
  paymentReviewDate?: string
  paymentRejectReason?: string
  prescriptionResponse?: {
    state: number
    pharmacy: {
      id: number
      name: string
      address: string
      telphon: string
      pharmacyCity?: string
    }
  }[]
}

export interface PrescriptionDraft {
  step: number
  provinceCode: string
  provinceName: string
  cityCode: string
  cityName: string
  allowOtherProvinces: boolean | null
  comment: string
  isSath3: boolean
  pharmacyCode: string
  image: string
  imageName: string
}

export const emptyDraft: PrescriptionDraft = {
  step: 0,
  provinceCode: '',
  provinceName: '',
  cityCode: '',
  cityName: '',
  allowOtherProvinces: null,
  comment: '',
  isSath3: false,
  pharmacyCode: '',
  image: '',
  imageName: '',
}

// The API returns Tehran wall-clock timestamps without an offset.
export const tehranTime = (value?: string): number => {
  if (!value) return NaN
  const normalized = value.replace(' ', 'T')
  return Date.parse(
    /(Z|[+-]\d{2}:?\d{2})$/i.test(normalized)
      ? normalized
      : `${normalized}+03:30`,
  )
}

export const requestStatus = (
  item: PublicPrescription,
  now: number,
): { active: boolean; kind: string; label: string } => {
  if (item.cancelDate)
    return { active: false, kind: 'cancelled', label: 'لغو شده' }
  if (item.paymentStatus === 2)
    return { active: true, kind: 'review', label: 'رسید شما در حال بررسی است' }
  if (item.paymentStatus === 1 || item.paymentStatus === 4) {
    if (!Number.isFinite(tehranTime(item.paymentDueDate)))
      return {
        active: true,
        kind: 'unknown-payment',
        label: 'در انتظار اطلاعات مهلت پرداخت',
      }
    return now >= tehranTime(item.paymentDueDate)
      ? { active: false, kind: 'payment-expired', label: 'مهلت پرداخت تمام شد' }
      : {
          active: true,
          kind: 'payment',
          label: 'داروی شما پیدا شد؛ منتظر پرداخت',
        }
  }
  if (
    item.paymentStatus === 3 ||
    (item.prescriptionResponse || []).some((response) => response.state === 2)
  ) {
    return { active: true, kind: 'found', label: 'دارو پیدا شد' }
  }
  if (now >= tehranTime(item.expireDate))
    return { active: false, kind: 'expired', label: 'دارو پیدا نشد' }
  return { active: true, kind: 'search', label: 'در حال جستجو در داروخانه‌ها' }
}

export const validateDrug = (value: string): string => {
  const name = value.trim()
  if (!name)
    return 'نام دارو الزامی است؛ ارسال تصویر به تنهایی برای ثبت درخواست کافی نیست'
  if (name.length > 200)
    return 'نام دارو بیش از ۲۰۰ نویسه است؛ فقط نام دارو و در صورت اطلاع دوز را بنویسید'
  if (
    /[\n\r,،;؛]/.test(name) ||
    name
      .split(/[+/|]/)
      .filter((part) =>
        /[a-zA-Z\u0621-\u063A\u0641-\u064A\u067E\u0686\u0698\u06A9\u06AF\u06CC]/.test(
          part.replace(/\b(?:mg|mcg|g|ml|iu|units?)\b/gi, ''),
        ),
      ).length > 1
  )
    return 'در هر درخواست فقط یک دارو ثبت می‌شود؛ برای هر دارو یک درخواست جداگانه ثبت کنید'
  if (
    (
      name.match(
        /[a-zA-Z\u0621-\u063A\u0641-\u064A\u067E\u0686\u0698\u06A9\u06AF\u06CC]/g,
      ) || []
    ).length < 2
  )
    return 'نام دارو معتبر نیست؛ نام دارو را با حروف فارسی یا انگلیسی بنویسید'
  return ''
}

export const validateImage = (file: File, maxMB: number): string => {
  if (!['image/jpeg', 'image/png'].includes(file.type))
    return 'فقط تصویر JPG یا PNG انتخاب کنید.'
  if (file.size > maxMB * 1024 * 1024)
    return `حجم تصویر باید حداکثر ${maxMB.toLocaleString(
      'fa-IR',
    )} مگابایت باشد.`
  return ''
}

export const persianDate = (value: string): string =>
  Number.isFinite(tehranTime(value))
    ? new Date(tehranTime(value)).toLocaleDateString('fa-IR', {
        timeZone: 'Asia/Tehran',
      })
    : '—'
export const toman = (rial: number): string =>
  `${(rial / 10).toLocaleString('fa-IR')} تومان`
